import staffRowExpanderStyles from './staff-row-expander.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { SiteAssignmentsApi } from '../../../api/security/site-assignments-api';
import { ApiSiteMaintenance } from '../../../api/core/api-site-maintenance';
import type {
  MemberSiteListItem,
  UserSiteAssignmentDto,
} from '../../../api/agwa-api-model';
import { confirmAction } from '../../../components/dialogs/agwa-confirm-dialog';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import { terms } from '../../../language/terms';
import {
  collapseOperationTypeCodes,
  getOrganizationTypeLabel,
} from '../../app-members/shared/organization-type-labels';

import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/** Site row for display */
interface SiteRow {
  siteId: string;
  siteName: string;
  active: boolean;
  organizationTypes: string[];
  /** Set when user is assigned to this site */
  assignmentId?: string;
  allSites?: boolean;
}

/**
 * What the checkbox says, composed rather than spelled.
 *
 * The concept is named once, in the glossary, so AGWA correcting the words is one edit
 * (CLAUDE.md, "Domain Words Belong to Terms, Never to a Template"). Held as module constants so the
 * template carries a single expression and no stray whitespace can creep into either string - both
 * are asserted character for character by `@PIP-EDIT-001`.
 */
const PIP_EDIT_LABEL = `Can Edit ${terms.primeSystemIntellectualProperty.value}`;

/**
 * Why a person would want the box ticked, in the terms the rest of the product uses.
 *
 * It names the MODULE (`productRegistry`) and then what is in it, because "Prime System IP" is
 * AGWA's word for the thing and not the reader's: the person deciding whether to tick this needs to
 * know it hands over the company's products and test reports (D-127 rule 6).
 */
const PIP_EDIT_HINT = `Grants this ${terms.loginUser.lower} the ${terms.productRegistry.value}: the ${terms.loginGroup.lowerPossessive} ${terms.product.lowerPlural} and ${terms.productTestReport.lowerPlural}.`;

@customElement('staff-row-expander')
export class StaffRowExpander extends LitElement {
  static styles = [sharedStyles, unsafeCSS(staffRowExpanderStyles)];

  @property({ type: String }) userId = '';
  @property({ type: String }) groupId = '';
  @property({ type: String }) userName = '';

  /**
   * Which read of the company's roster the host last made, so an open panel re-reads with it
   * (#398 C9). The staff list re-reads its roster whenever someone in the company is assigned to a
   * site or a site's types change, possibly in another browser, and this panel shows exactly those
   * things for one person. It used to be refreshed by accident: the table rebuilt every row on
   * each render, so the panel was destroyed and mounted afresh. The table now keeps its rows, so
   * the host says so instead; a change here re-reads quietly, keeping what is shown until the
   * answer arrives.
   */
  @property({ type: Number }) rosterRevision = 0;

  /** The newest read asked for; an answer to an older one is dropped (see loadData). */
  private readToken = 0;

  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private allSites: MemberSiteListItem[] = [];
  /**
   * The company's sites the product never presents as places - today exactly its Prime System IP
   * site, if it has a product register at all (D-127 rule 4).
   *
   * Kept apart from `allSites` on purpose, because that field feeds both site lists and both counts:
   * a hidden site that reached it would appear as a row the company never made. This one is read by
   * the "Can Edit Prime System IP" checkbox and by nothing else.
   */
  @state() private hiddenSites: MemberSiteListItem[] = [];
  @state() private userAssignments: UserSiteAssignmentDto[] = [];
  @state() private actionInProgress: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.userId && this.groupId) {
      void this.loadData();
    }
  }

  updated(changedProps: Map<string, unknown>) {
    if (
      (changedProps.has('userId') || changedProps.has('groupId')) &&
      this.userId &&
      this.groupId
    ) {
      void this.loadData();
    } else if (
      changedProps.has('rosterRevision') &&
      this.userId &&
      this.groupId
    ) {
      this.requestQuietReread();
    }
  }

  /** Reads started and not yet answered. */
  private readsInFlight = 0;

  /** A roster re-read asked for while another read was in flight, run when that read answers. */
  private quietRereadWanted = false;

  /**
   * Re-read for the roster, never over the top of a read already in flight (#398 C9 review 2).
   * A quiet read that superseded an in-flight one and then failed would leave neither answer
   * applied and no error shown: the panel opening, or the one after the panel's own Add or Remove,
   * would be lost silently. So it waits for that read and runs after it, which is also when the
   * roster change it was asked for is certain to be in what it reads.
   */
  private requestQuietReread() {
    if (this.readsInFlight > 0) {
      this.quietRereadWanted = true;
      return;
    }
    void this.loadData({ quiet: true });
  }

  /**
   * Read the company's sites and this person's assignments.
   *
   * `quiet` is a re-read the host's roster asked for: the panel keeps what it shows, with no
   * spinner, and a failure keeps it too rather than replacing it with an error. A quiet read is
   * never started while another read is out (requestQuietReread), so it cannot overtake one; a loud
   * read can overtake a quiet one, and then whichever was asked for last wins, whatever order the
   * answers arrive in.
   */
  private async loadData(options: { quiet?: boolean } = {}) {
    const quiet = options.quiet === true;
    const token = ++this.readToken;
    this.readsInFlight++;
    if (!quiet) {
      this.loading = true;
      this.error = null;
    }

    try {
      const [sitesResponse, assignmentsResponse] = await Promise.all([
        ApiFactory.authenticated(ApiSiteMaintenance).getMemberSites(
          this.groupId
        ),
        ApiFactory.authenticated(SiteAssignmentsApi).getUserAssignments(
          this.userId
        ),
      ]);
      // Overtaken by a later read: its answer describes a moment that is no longer the latest.
      if (token !== this.readToken) return;

      if (!sitesResponse.success || !sitesResponse.data?.items) {
        throw new Error(
          sitesResponse.error ||
            `Unable to load ${terms.loginSite.lowerPlural}.`
        );
      }
      // A quiet re-read that could not read the assignments keeps the ones shown, rather than
      // reading as a person with none.
      if (quiet && !assignmentsResponse.success) return;

      this.allSites = sitesResponse.data.items;
      this.hiddenSites = sitesResponse.data.hiddenItems ?? [];
      // The read returns ACTIVE assignments only (`includeInactive` defaults to false), which is
      // what lets the checkbox below read "is this person assigned" straight off the list.
      this.userAssignments = assignmentsResponse.success
        ? (assignmentsResponse.data?.assignments ?? [])
        : [];
    } catch (e) {
      // A quiet re-read that fails keeps what is shown; the next roster change brings another.
      if (!quiet && token === this.readToken) {
        this.error = (e as Error)?.message ?? 'Unable to load data.';
      }
    } finally {
      // Only the newest read may say loading has stopped.
      if (token === this.readToken) this.loading = false;
      this.readsInFlight--;
      // A roster re-read that waited for this one runs now.
      if (this.readsInFlight === 0 && this.quietRereadWanted) {
        this.quietRereadWanted = false;
        void this.loadData({ quiet: true });
      }
    }
  }

  private buildRows(): { assigned: SiteRow[]; available: SiteRow[] } {
    const assignmentMap = new Map(
      this.userAssignments.map((a) => [a.loginSiteId, a])
    );

    const assigned: SiteRow[] = [];
    const available: SiteRow[] = [];

    for (const site of this.allSites) {
      const assignment = assignmentMap.get(site.siteId);
      const row: SiteRow = {
        siteId: site.siteId,
        siteName: site.siteName,
        active: site.active,
        organizationTypes: site.organizationTypes ?? [],
        assignmentId: assignment?.id,
        allSites: assignment?.allSites,
      };

      if (assignment) {
        assigned.push(row);
      } else if (site.active) {
        // Only show active sites in available list
        available.push(row);
      }
    }

    return { assigned, available };
  }

  /**
   * The site the "Can Edit Prime System IP" checkbox is about, or null when the company has none.
   *
   * TAKEN AS "THE HIDDEN SITE", NOT "THE SITE CARRYING THE PIP CODE". The two are the same site
   * while the company's register is awake, but rule 3 lets a register sleep - unregister the last
   * supplier and the hidden site is deactivated along with its scope, so its `organizationTypes`
   * arrive empty. Keying on the code would make the checkbox vanish at that moment, taking a ticked
   * person's grant off the screen while the assignment still exists and still comes back with the
   * register. The hidden list carries at most one site per company (D-127 rule 1, one per company
   * guaranteed by the database), so the first is the only.
   */
  private get primeSystemIpSite(): MemberSiteListItem | null {
    return this.hiddenSites[0] ?? null;
  }

  /**
   * The assignment that IS the right to edit the company's Prime System IP, if this person holds it.
   *
   * Matched on the site id alone, exactly as `buildRows` matches the visible sites: an all-sites row
   * cannot name the hidden site (`JobAssignUserToSite` picks a visible site for it, deliberately),
   * so there is no second case to distinguish here.
   */
  private primeSystemIpAssignment(): UserSiteAssignmentDto | undefined {
    const site = this.primeSystemIpSite;
    if (!site) return undefined;
    return this.userAssignments.find((a) => a.loginSiteId === site.siteId);
  }

  /**
   * Tick or untick the right to edit the company's Prime System IP.
   *
   * THIS IS A SITE ASSIGNMENT IN FRESH CLOTHES (the architect's phrase, D-127 rule 6): it goes
   * through the same two jobs the Add and Remove buttons use, so there is one grant mechanism, one
   * audit row and one set of rules about who may do it - not a second, parallel way to hand out the
   * product register. Only the presentation differs, because the hidden site must never appear as a
   * place the company has.
   *
   * Both directions confirm, as the rows do, and NEITHER CONFIRMATION NAMES THE SITE: "{Company} -
   * PIP" is a name no person has ever been shown and rule 4 keeps it that way, so the words are
   * about what the person gains or loses instead.
   */
  private async handlePrimeSystemIpToggle(checked: boolean) {
    const site = this.primeSystemIpSite;
    if (!site) return;

    const assignment = this.primeSystemIpAssignment();
    // Already in the state being asked for: nothing to do, but re-render so the control reflects
    // what is held rather than what was clicked.
    if (checked === (assignment !== undefined)) {
      this.requestUpdate();
      return;
    }

    const confirmed = await confirmAction(
      checked
        ? {
            title: `Grant ${terms.primeSystemIntellectualProperty.value}`,
            message: `Let "${this.userName}" edit the ${terms.loginGroup.lowerPossessive} ${terms.product.lowerPlural} and ${terms.productTestReport.lowerPlural}?`,
            confirmText: 'Grant',
            cancelText: 'Cancel',
            variant: 'primary',
          }
        : {
            title: `Remove ${terms.primeSystemIntellectualProperty.value}`,
            message: `Stop "${this.userName}" editing the ${terms.loginGroup.lowerPossessive} ${terms.product.lowerPlural} and ${terms.productTestReport.lowerPlural}?`,
            confirmText: 'Remove',
            cancelText: 'Cancel',
            variant: 'warning',
          }
    );

    // A cancelled toggle must put the box back. `live()` on the binding below is what makes this
    // re-render undo it: a plain `?checked` binding is dirty-checked against the value it last
    // committed, which has not changed, so Lit would leave the person's click standing as a tick
    // that grants nothing.
    if (!confirmed) {
      this.requestUpdate();
      return;
    }

    this.actionInProgress = site.siteId;
    try {
      const api = ApiFactory.authenticated(SiteAssignmentsApi);
      const response = checked
        ? await api.assignUserToSite({
            loginUserId: this.userId,
            loginSiteId: site.siteId,
            allSites: false,
          })
        : await api.removeAssignment(assignment!.id);

      if (!response.success) {
        throw new Error(
          response.error ||
            `Failed to change who may edit the ${terms.loginGroup.lowerPossessive} ${terms.primeSystemIntellectualProperty.value}.`
        );
      }

      // This panel's own state only. THE ROSTER ABOVE IS NOT TOLD FROM HERE: the server announces
      // the assignment on the company's topic and every open staff list re-reads itself, including
      // this browser's (staff-operation-types-acceptance.md, rule 7). A DOM event alongside it
      // would be a second mechanism for one job, and the one that cannot reach the other screens
      // this change is visible on.
      await this.loadData();
    } catch (e) {
      this.error =
        (e as Error)?.message ??
        `Failed to change who may edit the ${terms.loginGroup.lowerPossessive} ${terms.primeSystemIntellectualProperty.value}.`;
    } finally {
      this.actionInProgress = null;
    }
  }

  private async handleAdd(site: SiteRow) {
    const confirmed = await confirmAction({
      title: `Add to ${terms.loginSite.value}`,
      message: `Assign "${this.userName}" to ${site.siteName}?`,
      confirmText: 'Add',
      cancelText: 'Cancel',
      variant: 'primary',
    });

    if (!confirmed) return;

    this.actionInProgress = site.siteId;
    try {
      const api = ApiFactory.authenticated(SiteAssignmentsApi);
      const response = await api.assignUserToSite({
        loginUserId: this.userId,
        loginSiteId: site.siteId,
        allSites: false,
      });

      if (!response.success) {
        throw new Error(
          response.error || `Failed to add to ${terms.loginSite.lower}.`
        );
      }

      // This panel's own state only - the roster above re-reads itself from the server's
      // announcement, not from here. See the note in `handlePrimeSystemIpToggle`.
      await this.loadData();
    } catch (e) {
      this.error =
        (e as Error)?.message ?? `Failed to add to ${terms.loginSite.lower}.`;
    } finally {
      this.actionInProgress = null;
    }
  }

  private async handleRemove(site: SiteRow) {
    if (!site.assignmentId) return;

    const confirmed = await confirmAction({
      title: `Remove from ${terms.loginSite.value}`,
      message: `Remove "${this.userName}" from ${site.siteName}?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'warning',
    });

    if (!confirmed) return;

    this.actionInProgress = site.siteId;
    try {
      const api = ApiFactory.authenticated(SiteAssignmentsApi);
      const response = await api.removeAssignment(site.assignmentId);

      if (!response.success) {
        throw new Error(
          response.error || `Failed to remove from ${terms.loginSite.lower}.`
        );
      }

      // This panel's own state only - the roster above re-reads itself from the server's
      // announcement, not from here. See the note in `handlePrimeSystemIpToggle`.
      await this.loadData();
    } catch (e) {
      this.error =
        (e as Error)?.message ??
        `Failed to remove from ${terms.loginSite.lower}.`;
    } finally {
      this.actionInProgress = null;
    }
  }

  render() {
    if (this.loading) {
      return html`
        <div class="loading-container">
          <sl-spinner></sl-spinner>
          <span>Loading ${terms.loginSite.lower} assignments...</span>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          ${this.error}
        </sl-alert>
      `;
    }

    const { assigned, available } = this.buildRows();

    return html`
      <div class="expander-content">
        ${this.renderPrimeSystemIpGrant()}
        <div class="two-columns">
          ${this.renderAssignedSection(assigned)}
          ${this.renderAvailableSection(available)}
        </div>
      </div>
    `;
  }

  /**
   * The one checkbox that says whether this person may edit their company's Prime System IP.
   *
   * ABOVE THE TWO COLUMNS AND OUTSIDE BOTH, because it is not a site and must not read as one. The
   * company's register is held on a site the company never sees, so the grant belongs to the person
   * and the company rather than to any place in either list - and a person whose ONLY assignment is
   * that hidden site correctly sees "Not assigned to any sites." beside a ticked box
   * (`@PIP-HIDE-003`). It carries no heading of its own for the same reason: a title over it would
   * be a second thing to read as a section of sites.
   *
   * Offered whenever the company HAS a hidden site, including while its register sleeps, so a
   * ticked person's grant stays visible and revocable rather than disappearing with the register it
   * will get back (rule 3). A company that never registered a supplier or manufacturer has no hidden
   * site and is offered nothing at all (`@PIP-EDIT-004`).
   */
  private renderPrimeSystemIpGrant() {
    const site = this.primeSystemIpSite;
    if (!site) return nothing;

    const ticked = this.primeSystemIpAssignment() !== undefined;
    const inProgress = this.actionInProgress === site.siteId;

    return html`
      <div class="pip-grant">
        <sl-checkbox
          class="pip-edit"
          .checked=${live(ticked)}
          ?disabled=${inProgress}
          @sl-change=${(event: Event) =>
            void this.handlePrimeSystemIpToggle(
              (event.target as HTMLInputElement).checked
            )}
          >${PIP_EDIT_LABEL}</sl-checkbox
        >
        <!--
          NOT hoisted, matching site-operation-types-section: hoisting is for escaping a clipping
          ancestor and this row has none, and keeping the popup in place keeps the explanation part
          of the control it explains, in the accessibility tree and to anything reading the DOM.
        -->
        <sl-tooltip content=${PIP_EDIT_HINT}>
          <sl-icon
            class="pip-edit-info"
            name="info-circle"
            label="About ${PIP_EDIT_LABEL}"
          ></sl-icon>
        </sl-tooltip>
        ${inProgress ? html`<sl-spinner></sl-spinner>` : nothing}
      </div>
    `;
  }

  private renderAssignedSection(sites: SiteRow[]) {
    return html`
      <div class="section">
        <div class="section-header">
          <h4 class="section-title">Assigned ${terms.loginSite.plural}</h4>
          <span class="count-badge">${sites.length}</span>
        </div>
        ${
          sites.length > 0
            ? html`
                <div class="site-list">
                  ${sites.map((site) => this.renderSiteRow(site, 'remove'))}
                </div>
              `
            : html`<div class="empty-state">
                Not assigned to any ${terms.loginSite.lowerPlural}.
              </div>`
        }
      </div>
    `;
  }

  private renderAvailableSection(sites: SiteRow[]) {
    return html`
      <div class="section">
        <div class="section-header">
          <h4 class="section-title">Available ${terms.loginSite.plural}</h4>
          <span class="count-badge">${sites.length}</span>
        </div>
        ${
          sites.length > 0
            ? html`
                <div class="site-list">
                  ${sites.map((site) => this.renderSiteRow(site, 'add'))}
                </div>
              `
            : html`<div class="empty-state">
                No more ${terms.loginSite.lowerPlural} available.
              </div>`
        }
      </div>
    `;
  }

  private renderSiteRow(site: SiteRow, action: 'add' | 'remove') {
    const inProgress = this.actionInProgress === site.siteId;

    return html`
      <div class="site-row">
        <div class="site-info">
          <span class="site-name">${site.siteName}</span>
          <div class="site-meta">
            ${
              site.organizationTypes.length > 0
                ? html`
                    <div class="org-types">
                      ${collapseOperationTypeCodes(site.organizationTypes).map(
                        // The DTO carries the stable domain code; the word is resolved here,
                        // as everywhere else, so the code never reaches the screen. PSS
                        // collapses to PSM anywhere it is shown (architect, 2026-09-17).
                        (type) =>
                          html`<sl-tag size="small"
                            >${getOrganizationTypeLabel(type)}</sl-tag
                          >`
                      )}
                    </div>
                  `
                : ''
            }
            ${
              site.allSites
                ? html`<sl-tag size="small" variant="primary"
                    >All ${terms.loginSite.plural}</sl-tag
                  >`
                : ''
            }
          </div>
        </div>
        ${
          action === 'add'
            ? html`
                <sl-button
                  size="small"
                  variant="primary"
                  outline
                  ?loading=${inProgress}
                  ?disabled=${inProgress}
                  @click=${() => this.handleAdd(site)}
                >
                  <sl-icon slot="prefix" name="plus"></sl-icon>
                  Add
                </sl-button>
              `
            : html`
                <sl-button
                  size="small"
                  variant="danger"
                  outline
                  ?loading=${inProgress}
                  ?disabled=${inProgress}
                  @click=${() => this.handleRemove(site)}
                >
                  <sl-icon slot="prefix" name="x"></sl-icon>
                  Remove
                </sl-button>
              `
        }
      </div>
    `;
  }
}
